import { cn } from "@/lib/utils";

// Shared 9b "Full heartbeat" geometry on a fixed 48x48 grid: an infinity loop
// split by an ECG pulse through the waist. Reused by the on-tile mark here and
// the generated favicon/apple-icon so every surface renders the same glyph.
const LEFT_LOOP = "M16,24 C11,14.5 3,16.5 4,24 C5,31.5 12,33 16,24";
const RIGHT_LOOP = "M32,24 C37,14.5 45,16.5 44,24 C43,31.5 36,33 32,24";
const PULSE = "M16,24 L20,24 L22.5,20 L25,11 L27.5,34 L29.5,24 L32,24";

/**
 * Signalync logo mark (direction 9b, "Full heartbeat").
 *
 * Strokes inherit `currentColor`, so the glyph takes the surrounding text
 * color: white inside {@link LogoTile}, or a theme token when placed on its
 * own. Server-safe (no hooks) — renders identically in RSC and client trees.
 *
 * Pass `title` for a standalone, meaningful mark (adds `role="img"` + label);
 * omit it when a sibling wordmark already names the brand, so the glyph stays
 * decorative (`aria-hidden`).
 */
export function LogoMark({
  className,
  title,
}: {
  className?: string;
  title?: string;
}) {
  return (
    <svg
      viewBox="0 0 48 48"
      className={cn("size-full", className)}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {title ? <title>{title}</title> : null}
      <path d={LEFT_LOOP} strokeWidth={2.8} />
      <path d={RIGHT_LOOP} strokeWidth={2.8} />
      <path d={PULSE} strokeWidth={4.2} />
    </svg>
  );
}

/**
 * The logo mark on the brand gradient tile: a teal-to-violet rounded square
 * with the heartbeat glyph in white. This is the app-icon lockup — the same
 * shape emitted as the favicon.
 *
 * Uses the `--primary`/`--secondary` tokens (whose light/dark values are the
 * brand gradient stops), so it re-tints correctly per theme without hardcoded
 * hex. Sizing is driven by the tile's own `size-*` class; the glyph tracks it.
 */
export function LogoTile({
  className,
  title,
}: {
  className?: string;
  title?: string;
}) {
  return (
    <span
      className={cn(
        "grid aspect-square size-8 place-items-center rounded-[29%] bg-linear-to-br from-primary to-secondary text-white",
        className,
      )}
    >
      <LogoMark className="size-[68%]" title={title} />
    </span>
  );
}
